<div class="container-fluid h-100 px-4 px-md-5">
  <div class="my-4">

    <div class="page-header">
      <button class="back-btn" (click)="goBack()" type="button">← All traces</button>
    </div>

    <div *ngIf="loading" class="dashboard-loading">
      <div class="dashboard-spinner"></div>
    </div>

    <div *ngIf="!loading && error" class="state-msg state-msg--error">Failed to load this trace.</div>

    <ng-container *ngIf="!loading && !error && transaction">

      <div class="trace-header">
        <div class="trace-header__badges">
          <span class="method-badge">{{ transaction.http_method || transaction.op }}</span>
          <span class="code-badge" *ngIf="transaction.status_code">{{ transaction.status_code }}</span>
          <span class="status-badge" [class.status-badge--error]="transaction.status === 'error'">
            {{ transaction.status }}
          </span>
          <span class="platform-badge" *ngIf="transaction.environment">{{ transaction.environment }}</span>
          <span class="platform-badge" *ngIf="transaction.release">{{ transaction.release }}</span>
        </div>
        <h1 class="trace-header__title">{{ transaction.name }}</h1>
        <p class="trace-header__url">{{ transaction.url }}</p>
      </div>

      <div class="stats-row">
        <div class="stat-card">
          <span class="stat-card__label">Duration</span>
          <span class="stat-card__value">{{ formatDuration(transaction.duration_ms) }}</span>
        </div>
        <div class="stat-card">
          <span class="stat-card__label">Database</span>
          <span class="stat-card__value">{{ formatDuration(transaction.db_time_ms) }}</span>
          <span class="stat-card__sub">
            {{ transaction.db_span_count }} queries · {{ databaseShare | number:'1.0-0' }}% of request
          </span>
        </div>
        <div class="stat-card">
          <span class="stat-card__label">Spans</span>
          <span class="stat-card__value">{{ transaction.span_count | number }}</span>
        </div>
        <div class="stat-card">
          <span class="stat-card__label">Who</span>
          <span class="stat-card__value stat-card__value--small">
            {{ transaction.user_context?.email || transaction.user_context?.username ||
               transaction.user_context?.id || 'anonymous' }}
          </span>
          <span class="stat-card__sub" *ngIf="transaction.client_ip">{{ transaction.client_ip }}</span>
        </div>
        <div class="stat-card">
          <span class="stat-card__label">When</span>
          <span class="stat-card__value stat-card__value--small">
            {{ formatDateTime(transaction.start_timestamp) }}
          </span>
          <span class="stat-card__sub" *ngIf="transaction.server_name">{{ transaction.server_name }}</span>
        </div>
      </div>

      <!-- Waterfall ------------------------------------------------------- -->
      <section class="panel" *ngIf="waterfall.length">
        <div class="panel__header">
          <h2 class="panel__title">Timeline</h2>
          <button class="toggle-btn" type="button" (click)="showAllSpans = !showAllSpans"
            *ngIf="hiddenSpanCount > 0 || showAllSpans">
            {{ showAllSpans ? 'Show first ' + visibleSpanLimit : 'Show all (' + hiddenSpanCount + ' hidden)' }}
          </button>
        </div>

        <div class="waterfall">
          <div class="waterfall__row" *ngFor="let row of waterfall">
            <div class="waterfall__label">
              <span class="op-badge" [ngClass]="spanClass(row.span.op)">{{ row.span.op }}</span>
              <span class="waterfall__description" [title]="row.span.description">{{ row.span.description }}</span>
            </div>
            <div class="waterfall__track">
              <div class="waterfall__bar" [ngClass]="spanClass(row.span.op)"
                [style.margin-left.%]="row.offsetPercent" [style.width.%]="row.widthPercent"></div>
            </div>
            <div class="waterfall__duration">{{ formatDuration(row.span.duration_ms) }}</div>
          </div>
        </div>
      </section>

      <!-- Functions touched ----------------------------------------------- -->
      <section class="panel" *ngIf="transaction.function_spans?.length">
        <h2 class="panel__title">Functions touched</h2>
        <p class="panel__hint">
          Your own functions that ran during this request, slowest first. Times include nested calls.
        </p>
        <table class="table table-sm functions-table">
          <thead>
            <tr>
              <th>Function</th>
              <th class="text-end">Calls</th>
              <th class="text-end">Total</th>
              <th class="bar-column"></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let fn of transaction.function_spans">
              <td class="mono">{{ fn.description }}</td>
              <td class="text-end">{{ fn.calls | number }}</td>
              <td class="text-end duration-cell">{{ formatDuration(fn.duration_ms) }}</td>
              <td class="bar-column">
                <div class="bar"><div class="bar__fill" [style.width.%]="functionShare(fn.duration_ms)"></div></div>
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      <!-- Context --------------------------------------------------------- -->
      <div class="panel-row">
        <section class="panel panel--half">
          <h2 class="panel__title">Request</h2>
          <table class="kv-table">
            <tbody>
              <tr>
                <th>Method</th>
                <td>{{ transaction.http_method || '—' }}</td>
              </tr>
              <tr>
                <th>URL</th>
                <td class="break">{{ transaction.url }}</td>
              </tr>
              <tr *ngIf="transaction.request?.query_string">
                <th>Query</th>
                <td class="break">{{ transaction.request.query_string }}</td>
              </tr>
              <tr>
                <th>Trace ID</th>
                <td class="break mono">{{ transaction.trace_id }}</td>
              </tr>
              <tr *ngIf="transaction.sdk_name">
                <th>SDK</th>
                <td>{{ transaction.sdk_name }} {{ transaction.sdk_version }}</td>
              </tr>
              <tr *ngFor="let header of headerEntries">
                <th>{{ header.key }}</th>
                <td class="break">{{ header.value }}</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section class="panel panel--half" *ngIf="userEntries.length || tagEntries.length">
          <h2 class="panel__title">User & tags</h2>
          <table class="kv-table">
            <tbody>
              <tr *ngFor="let entry of userEntries">
                <th>{{ entry.key }}</th>
                <td class="break">{{ entry.value }}</td>
              </tr>
              <tr *ngFor="let entry of tagEntries">
                <th>{{ entry.key }}</th>
                <td class="break">{{ entry.value }}</td>
              </tr>
            </tbody>
          </table>
        </section>
      </div>

    </ng-container>

  </div>
</div>
